import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList as List } from 'react-window';
import cn from 'classnames';
import { current, produce } from 'immer';

import { SpinnerOverlay } from '@components/common';
import FlatTreeMavenirNodeWrapper
	from '@components/routes/CmModule/ConfigurationComparison/MOTreeMavenir/FlatTreeMavenir/FlatTreeMavenirNodeWrapper';

import { toggleFlatTree } from '@components/common/FlatTree/utils';
import { useModal } from '@components/common/Modal/utils';
import { getCountOfCheckedNodes } from '@components/Kpi/Parameters/ParametersKpiSelector/TreeGroupSelectWrapper/TreeGroupSelect/utils';

import { IFlatMoTreeNode, ITreeCheckboxOption } from '@interfaces/components/tree';
import { IMavenirSelectChangeCallback } from './interfaces';

import FlatTreeMavenirButtons from './FlatTreeMavenirButtons';
import {
	getAncestorsObjectNodes,
	getChangedMavenirSelect,
	getItemsSize,
	getNodePathWithMoIds,
	isCommonRootPath,
	isMavenirNodeObject,
	isMavenirNodeObjectsArray,
	recalculateCheckedNodes,
	toggleSelectedNodes,
} from './utils';

import styles from './FlatTreeMavenir.module.scss';

interface IFlatTreeMavenirProps {
	isReadOnly?: boolean;
	isWithReset?: boolean;
	isRevertReset?: boolean;
	submitHandler?: (data: IFlatMoTreeNode[]) => void;
	cancelHandler?: () => void;
	customCancelHandler?: () => void;
	isCloseWithConfirmation?: boolean;
	wrapperClassName?: string;
	submitText?: string;
	cancelText?: string;
	treeId?: string;
	isLoading?: boolean;
	getCheckboxLabel?: (value: boolean) => string;
	isSingleSelectMode?: boolean;
	updateConfig?: (data: IMavenirSelectChangeCallback) => void;
	isDisabledSubmit?: boolean;
	data: IFlatMoTreeNode[];
	defaultData?: IFlatMoTreeNode[];
	setData: React.Dispatch<React.SetStateAction<IFlatMoTreeNode[]>> | undefined;
	setIsDirtyForm?: (isDirty: boolean) => void;
	isWithFolderIcon?: boolean;
	isMaxSelectionError?: boolean;
	isIgnoreTreeChecked?: boolean;
	isDisabled?: boolean;
}

interface IChangeValues {
	checked: string[];
	selected: Map<string, {
		defaultValue: string;
		value: string;
	}>;
}

const FlatTreeMavenir = ({
	data,
	defaultData,
	setData,
	isReadOnly = false,
	isWithReset = false,
	isRevertReset = false,
	submitHandler,
	cancelHandler,
	customCancelHandler,
	getCheckboxLabel,
	setIsDirtyForm,
	treeId,
	isLoading,
	wrapperClassName,
	updateConfig,
	submitText,
	cancelText,
	isSingleSelectMode = false,
	isDisabledSubmit = false,
	isMaxSelectionError = false,
	isWithFolderIcon = false,
	isIgnoreTreeChecked = false,
	isDisabled = false,
	isCloseWithConfirmation = false,
}: IFlatTreeMavenirProps) => {
	const { onClose } = useModal();

	const [_changedValues, setChangedValues] = useState<IChangeValues>({ checked: [], selected: new Map() });
	const [isAbleToReset, setIsAbleToReset] = useState<boolean>(false);

	const defaultChangedValues: IChangeValues = useMemo(() => ({
		checked: [],
		selected: new Map(defaultData
			?.filter((item) => item.checkboxList.length > 0)
			?.map((item) => {
				return [
					item.keysPath!,
					{
						defaultValue: item.checkboxList[0].value,
						value: item.checkboxList[0].value,
					},
				];
			})),
	}), [defaultData]);

	const checkIsAbleToReset = useCallback((changedValues: IChangeValues) => {
		if (!defaultData) {
			setIsAbleToReset(false);

			return;
		}

		const isEqual = changedValues.checked.length === 0 && Array.from(changedValues.selected).every(([_, item]) => {
			return item.value === item.defaultValue;
		});

		setIsAbleToReset(!isEqual);
	}, [defaultData]);

	useEffect(() => {
		if (defaultData) {
			setChangedValues(defaultChangedValues);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const [isTreeChecked, setIsTreeChecked] = useState<boolean>(!!getCountOfCheckedNodes(data));

	const listRef = useRef();

	useEffect(() => {
		if (listRef.current) {
			(listRef.current as any).resetAfterIndex(0);
		}
	}, [data]);

	const isDisabledSubmitButton = isDisabledSubmit || (!isIgnoreTreeChecked && !isTreeChecked);
	const isDisabledCancel = cancelHandler ? false : !isTreeChecked;

	const toggleIsDirtyForm = useCallback((isDirty: boolean) => {
		if (setIsDirtyForm) {
			setIsDirtyForm(isDirty);
		}
	}, [setIsDirtyForm]);

	const resetChanges = useCallback(() => {
		if (defaultData) {
			setData?.(defaultData);
			setChangedValues(defaultChangedValues);
			setIsAbleToReset(false);

			return;
		}

		setData?.((prevData) =>
			produce(prevData, (draft) => {
				draft.forEach((node) => {
					node.isChecked = isRevertReset;
					node.countOfChecked = isRevertReset ? node.countOfChildren : 0;
				});
			}),
		);

		toggleIsDirtyForm(false);
	}, [
		isRevertReset,
		defaultData,
		defaultChangedValues,
		setData,
		toggleIsDirtyForm,
	]);

	const updateTreeData = useCallback(async ({
		dumpId,
		node,
		path,
	}: {
		node: IFlatMoTreeNode;
		path: string;
		dumpId: string;
	}) => {
		if (!node || !path || !updateConfig) return;

		try {
			await updateConfig({
				node,
				path,
				dumpId,
				setData: setData ? setData : () => null,
			});
			toggleIsDirtyForm(false);
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('Failed to update config:', error);
		}
	}, [updateConfig, setData, toggleIsDirtyForm]);

	const clearAllHandler = useCallback(() => {
		if (isCloseWithConfirmation && onClose) {
			onClose();

			return;
		}

		if (cancelHandler) {
			cancelHandler();
		}

		setData?.(
			produce((draftState) => {
				for (let index = 0; index < draftState.length; index++) {
					draftState[index].isChecked = false;
					draftState[index].countOfChecked = 0;
				}

				setIsTreeChecked(false);
			}),
		);
	}, [
		isCloseWithConfirmation,
		cancelHandler,
		onClose,
		setData,
	]);

	const updateCheckedNodes = useCallback((node: IFlatMoTreeNode) => {
		setChangedValues((prev) => {
			const data = ({
				...prev,
				checked: prev.checked.includes(node.id)
					? prev.checked.filter((value) => value !== node.id)
					: [...prev.checked, node.id],
			});

			checkIsAbleToReset(data);

			return data;
		});
	}, [setChangedValues, checkIsAbleToReset]);

	const updateSelected = useCallback((node: IFlatMoTreeNode, value: string) => {
		setChangedValues((prev) => {
			const newSelected = new Map(prev.selected);
			newSelected.set(node.keysPath!, {
				defaultValue: prev.selected.get(node.keysPath!)?.defaultValue || value,
				value,
			});

			const data = { ...prev, selected: newSelected };

			checkIsAbleToReset(data);

			return data;
		});
	}, [
		setChangedValues,
		checkIsAbleToReset,
	]);

	const getNodesPathsSelect = useCallback((node: IFlatMoTreeNode, checkedValue: string) => {
		const parentIndex = data.findIndex((item) => item.id === node.id);
		const parentCheckboxList = data[parentIndex]?.checkboxList || [];
		const isAllChecked = parentCheckboxList.every((item) => item.isChecked);

		const changedSelectCheckboxList: ITreeCheckboxOption[] = getChangedMavenirSelect({
			isAllChecked,
			checkedValue,
			parentCheckboxList: parentCheckboxList || [],
			isSingleSelectMode,
		});

		const isSomeNodeChecked = changedSelectCheckboxList.some((item) => item.isChecked);

		setData?.(
			produce((draftState) => {
				draftState[parentIndex].checkboxList = changedSelectCheckboxList;

				if (isSomeNodeChecked || isSingleSelectMode || parentIndex + 1 === draftState.length) return;

				for (let index = parentIndex + 1; index < draftState.length; index++) {
					const item = draftState[index];

					if (isCommonRootPath(item, node) && isMavenirNodeObjectsArray(item)) {
						draftState[index].checkboxList = [];
					}
				}

			}),
		);

		void updateTreeData({
			dumpId: treeId || '',
			path: getNodePathWithMoIds(data, node, changedSelectCheckboxList),
			node,
		});
	}, [updateTreeData, setData, treeId, data, isSingleSelectMode]);

	const checkSelect = useCallback((node: IFlatMoTreeNode, checkedValue: string) => {
		const updatedCheckboxList = node.checkboxList.map((item) => {
			return {
				...item,
				isChecked: item.value === checkedValue,
			};
		});

		const pathNode = isSingleSelectMode ? { ...node, checkboxList: updatedCheckboxList } : node;

		getNodesPathsSelect(pathNode, checkedValue);
		toggleIsDirtyForm(true);
		updateSelected(current(node), checkedValue);
	}, [
		isSingleSelectMode,
		toggleIsDirtyForm,
		getNodesPathsSelect,
		updateSelected,
	]);

	const collapseNodesHandler = useCallback((id: string, parentPath: string) => {
		setData?.(
			produce((draftState) => {
				toggleFlatTree(draftState, id, parentPath);
			}),
		);
	}, [setData]);

	const checkNodeHandler = useCallback((id: string) => {
		setData?.(
			produce((draftState) => {
				const parentIndex = draftState.findIndex((node) => node.id === id);

				const initCheckedCount = draftState[parentIndex].countOfChecked;

				toggleSelectedNodes({
					nodes: draftState,
					parentIndex,
				});

				const initNode = draftState[parentIndex];

				const parentPath = initNode.path;

				const nodesIndexes = data
					.reduce((acc: number[], node, index) => {
						return isMavenirNodeObject(node)
							? [...acc, index]
							: acc;
					}, []);

				const ancestorNodes = getAncestorsObjectNodes({
					parentPath,
					parentIndex,
					nodesIndexes,
					nodes: draftState,
				});

				recalculateCheckedNodes({
					ancestorNodes,
					initNode,
					initCheckedCount,
					setIsTreeChecked,
				});

				updateCheckedNodes(current(initNode));
			}),
		);

		toggleIsDirtyForm(true);
	}, [
		data,
		updateCheckedNodes,
		setIsTreeChecked,
		setData,
		toggleIsDirtyForm,
	]);

	const submitTreeData = useCallback(() => {
		if (submitHandler) {
			submitHandler(data);
		}
	}, [submitHandler, data]);

	const filteredData = useMemo(() => data.filter((node) => node.isHidden ? false : !node.isCollapsed), [data]);

	return (
		<div className={styles.container}>
			<div className={cn(styles.treeWrapper, wrapperClassName)}>
				{isLoading && <SpinnerOverlay />}
				<AutoSizer>
					{({ height = 0, width = 0 }) => (
						<List
							ref={listRef as any}
							height={height}
							itemCount={filteredData.length}
							itemData={filteredData}
							itemSize={(index: number) => getItemsSize(filteredData[index])}
							width={width}
						>
							{({ data, style, index }: any) => {
								return <FlatTreeMavenirNodeWrapper
									key={`node_${data[index].id}`}
									getCheckboxLabel={getCheckboxLabel}
									index={data[index].id}
									isDisabled={isMaxSelectionError ? !data[index].isChecked : isDisabled}
									isReadOnly={isReadOnly}
									isSingleSelectMode={isSingleSelectMode}
									isWithFolderIcon={isWithFolderIcon}
									node={data[index]}
									onCollapse={collapseNodesHandler}
									onSelect={checkSelect}
									onSkip={checkNodeHandler}
									style={style}
								/>;
							}}
						</List>
					)}
				</AutoSizer>
			</div>
			<FlatTreeMavenirButtons
				cancelHandler={customCancelHandler ? customCancelHandler : clearAllHandler}
				cancelText={cancelText}
				isDisabledCancel={isDisabledCancel}
				isDisabledReset={defaultData ? !isAbleToReset : isDisabledSubmitButton}
				isDisabledSubmit={isDisabledSubmitButton}
				resetChanges={isWithReset ? resetChanges : undefined}
				submitHandler={submitTreeData}
				submitText={submitText}
			/>
		</div>
	);
};

export default memo(FlatTreeMavenir);
